import { Alert, IconButton } from '@mui/material';
import { PropsWithChildren, useCallback, useEffect } from 'react';

import { Icon } from '@/components/Icon/Icon';
import { ErrorBoundary } from '@/components/Layout/ErrorBoundary';
import { DesktopOnly } from '@/components/Layout/Responsive';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';

import { selectUIState, showFileViewer } from '@/redux/ui';

import { SourceType } from '@/types/document';

import { getFileExtension } from '@/utils/file';

import { FileThumbnail } from './FileThumbnail';

import {
  FileFrameAction,
  FileThumbnailContainer,
  FileViewerContainer,
} from './File.styled';

type FileFrameProps = {
  className?: string;
  src: File | string;
  srcType?: SourceType;
  fullscreenOnly?: boolean;
  withViewButton?: boolean;
  onPDFScrolledToBottom?: () => void;
} & PropsWithChildren;

export function FileFrame({
  className,
  src,
  srcType,
  children,
  fullscreenOnly,
  withViewButton = true,
  onPDFScrolledToBottom,
}: FileFrameProps) {
  const { fileViewer } = useAppSelector(selectUIState);
  const dispatch = useAppDispatch();

  const shouldHideThumbnail =
    fileViewer?.type === SourceType.Web && fileViewer.src === src;

  const viewFile = useCallback(() => {
    let type = srcType || SourceType.Image;

    if (typeof src !== 'string') {
      const ext = getFileExtension(src);
      type = ext === '.pdf' ? SourceType.PDF : SourceType.Image;
    }

    dispatch(
      showFileViewer({
        src: typeof src === 'string' ? src : URL.createObjectURL(src),
        type,
      }),
    );
  }, [dispatch, src, srcType]);

  useEffect(() => {
    if (fullscreenOnly) {
      viewFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (fullscreenOnly) return null;

  return (
    <FileViewerContainer className={className}>
      {!shouldHideThumbnail && (
        <ErrorBoundary
          fallback={() => (
            <FileThumbnailContainer>
              <Alert
                icon={<Icon name="alert" />}
                color="error"
                variant="standard"
              >
                Something went wrong, cannot display document
              </Alert>
            </FileThumbnailContainer>
          )}
        >
          <>
            <FileThumbnail
              src={src}
              type={srcType}
              onPDFScrolledToBottom={onPDFScrolledToBottom}
            />
            {withViewButton && (
              <DesktopOnly>
                <FileFrameAction>
                  <IconButton onClick={viewFile}>
                    <Icon name="zoom-in" />
                  </IconButton>
                </FileFrameAction>
              </DesktopOnly>
            )}
          </>
        </ErrorBoundary>
      )}
      {children}
    </FileViewerContainer>
  );
}
